/**
 * Staff radio detail view — deep-dive info for a single cellular/radio unit.
 *
 * Reached from the support radio search at /support/radios/:id.  Shows
 * device identity, current location, carrier (Kore) connectivity status,
 * billing / prepaid coverage, and a full unified timeline.
 *
 * Data-fetching, Redux wiring, and business logic are owned by the class
 * component below. This file is styling-only for theme-new: section cards,
 * responsive layout, semantic tokens.
 *
 * Contains:
 * - BillingSection  — trial status + prepaid coverage panel
 * - RadioDetailView — class component (route entry point)
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { isBefore, parse } from 'date-fns';
import { t } from 'i18next';
import { List, Map } from 'immutable';
import moment from 'moment';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import AssignOrCreateDialog from '@sophocles/components/dialogs/AssignOrCreateDialog';
import MoveInventoryDialog from '@sophocles/components/dialogs/MoveInventoryDialog';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import StatusChip from '@sophocles/components/StatusChip';
import { fetchKoreSync, postKoreProvision } from '@sophocles/entities/KoreSubscription/actions';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { editRadioLocation } from '@sophocles/features/inventory/radioLocations/api';
import { fetchRadio, fetchRadioTimeline, radioSpecificSearch } from '@sophocles/features/radio/radioActions';
import { addToast } from '@sophocles/features/toast';
import {
  determineIsAccounting,
  determineIsAtLeastSiteGroupAdmin,
  determineIsInventoryAdmin,
  determineIsODLEngineering,
  determineIsSales,
  determineIsTechSupport,
} from '@sophocles/utils';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import PrepaidCoverageCard from '@sophocles/views/support/components/PrepaidCoverageCard';
import RadioLocation from '@sophocles/views/support/components/RadioLocation';
import UnifiedTimelineTable from '@sophocles/views/support/components/UnifiedTimelineTable';

// Shared sx for the inset bordered section panels — matches the
// SettingsSection / ContentSection look used across theme-new pages.
const sectionSx = {
  backgroundColor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 'var(--radius-lg)',
  px: { xs: 2, md: 3 },
  py: 2.5,
};

function SectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {children}
    </Typography>
  );
}

function BillingSection({ radio, radioId, onUpdate, isStaff }) {
  const radioJs = radio?.toJS ? radio.toJS() : radio;
  const billing = radioJs?.billing;
  const trialStart = billing?.trial_start_date;
  const trialEnd = billing?.trial_end_date;
  const trialEndDate = trialEnd ? parse(trialEnd, 'yyyy-MM-dd', new Date()) : null;
  const trialExpired = trialEndDate && isBefore(trialEndDate, new Date());
  const hasTrial = trialStart && trialEnd;

  return (
    <Box sx={{ ...sectionSx, mb: 2 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        {t('support.billing', 'Billing')}
      </Typography>
      <Divider sx={{ mb: 2.5 }} />
      <Grid container spacing={3}>
        {/* Trial */}
        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
          <SectionTitle>Trial</SectionTitle>
          {hasTrial ? (
            <Stack spacing={0.5}>
              <StatusChip
                label={trialExpired ? 'Expired' : 'Active'}
                color={trialExpired ? 'bad' : 'ok'}
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography variant="body2">
                {trialStart} &mdash; {trialEnd}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('support.noTrial', 'No trial')}
            </Typography>
          )}
        </Grid>

        {/* Prepaid Coverage */}
        <Grid size={{ xs: 12, sm: 8, md: 9 }}>
          <PrepaidCoverageCard
            coverages={radio.getIn ? radio.getIn(['billing', 'prepaid_coverages']) : null}
            radioId={radioId}
            onUpdate={onUpdate}
            isStaff={isStaff}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

class RadioDetailView extends React.Component {
  constructor(props) {
    super(props);

    const { user } = props;
    const groups = user.get('groups', []);
    const isSuperUser = user.get('is_superuser', false);

    const isSales = determineIsSales(groups);
    const isAccounting = determineIsAccounting(groups);

    this.state = {
      radio: Map(),
      radioSearch: Map(),
      radioFetching: true,
      radioSearchFetching: false,
      timelineData: List(),
      timelineFetching: false,
      isArchiving: false,
      openAssignDialog: false,
      openMoveDialog: false,
      canSeeBilling: isSales || isSuperUser || isAccounting,
      // Kore state
      koreLoading: false,
      koreError: null,
      koreSynced: false,
      koreActionMessage: null,
      showOmniWarningDialog: false,
      pendingKoreAction: null,
    };
  }

  componentDidMount() {
    this.reload();
  }

  reload = () => {
    const {
      fetchRadio,
      fetchRadioTimeline,
      match: { params },
      radioSpecificSearch,
    } = this.props;
    const radioId = params.radio_id;

    fetchRadio(radioId).then(res => {
      this.setState({ radioFetching: false });
      if (res.error) {
        this.props.addToast({
          title: 'Radio Load Failed',
          message: 'Could not load radio details. Try refreshing the page.',
          level: 'error',
          dismissAfter: 5000,
        });
      } else {
        this.setState({ radio: res, radioSearchFetching: true });
        radioSpecificSearch(res.get('iccid', null)).then(res => {
          this.setState({ radioSearchFetching: false });
          res.error
            ? this.props.addToast({
                title: 'Location Load Failed',
                message: 'Could not load radio location data. Try refreshing the page.',
                level: 'error',
                dismissAfter: 5000,
              })
            : this.setState({ radioSearch: res });
        });
      }
    });

    this.setState({ timelineFetching: true });
    fetchRadioTimeline(radioId).then(res => {
      this.setState({ timelineFetching: false });
      if (res && !res.error) {
        this.setState({ timelineData: res.get('results', List()) });
      }
    });
  };

  isOmniSim = iccid => {
    return iccid && iccid.startsWith('891039');
  };

  isVerizonSim = iccid => {
    return iccid && iccid.startsWith('891480');
  };

  handleKoreSync = () => {
    const { fetchKoreSync, addToast } = this.props;
    const { radio } = this.state;
    const iccid = radio.get('iccid', null);

    if (!iccid) {
      this.setState({ koreError: 'No ICCID available for this radio' });
      return;
    }

    this.setState({ koreLoading: true, koreError: null, koreActionMessage: null });

    fetchKoreSync(iccid).then(result => {
      if (result.success) {
        // Update local radio state with new kore_status and kore_verifed_at
        const updatedRadio = this.state.radio
          .set('kore_status', result.data.kore_status)
          .set('kore_verifed_at', result.data.kore_verifed_at);
        this.setState({
          radio: updatedRadio,
          koreLoading: false,
          koreSynced: true,
          koreError: null,
        });
        addToast({
          title: 'Sync Complete',
          message: 'Kore status has been updated.',
          level: 'success',
          dismissAfter: 3000,
        });
      } else {
        this.setState({
          koreLoading: false,
          koreError: result.error,
        });
        addToast({
          title: 'Sync Failed',
          message: result.error,
          level: 'error',
          dismissAfter: 5000,
        });
      }
    });
  };

  handleKoreAction = action => {
    const { radio } = this.state;
    const iccid = radio.get('iccid', null);

    // Check for OmniSIM warning
    if (action === 'deactivate' && this.isOmniSim(iccid)) {
      this.setState({ showOmniWarningDialog: true, pendingKoreAction: action });
      return;
    }

    this.executeKoreAction(action);
  };

  executeKoreAction = action => {
    const { postKoreProvision, addToast } = this.props;
    const { radio } = this.state;
    const iccid = radio.get('iccid', null);

    if (!iccid) {
      this.setState({ koreError: 'No ICCID available for this radio' });
      return;
    }

    this.setState({
      koreLoading: true,
      koreError: null,
      koreActionMessage: null,
      showOmniWarningDialog: false,
      pendingKoreAction: null,
    });

    postKoreProvision(iccid, action).then(result => {
      if (result.success) {
        this.setState({
          koreLoading: false,
          koreSynced: false, // Require re-sync to see updated status
          koreActionMessage: 'Action sent to Kore. Check back in 5-15 minutes for updated status.',
        });
        addToast({
          title: 'Action Sent',
          message: `${action.charAt(0).toUpperCase() + action.slice(1)} request sent to Kore.`,
          level: 'success',
          dismissAfter: 5000,
        });
      } else {
        this.setState({
          koreLoading: false,
          koreError: result.error,
        });
        addToast({
          title: 'Action Failed',
          message: result.error,
          level: 'error',
          dismissAfter: 5000,
        });
      }
    });
  };

  cancelOmniWarning = () => {
    this.setState({ showOmniWarningDialog: false, pendingKoreAction: null });
  };

  confirmOmniWarning = () => {
    const { pendingKoreAction } = this.state;
    if (pendingKoreAction) {
      this.executeKoreAction(pendingKoreAction);
    }
  };

  handleRestore = () => {
    const { editRadioLocation, addToast } = this.props;
    const { radio } = this.state;
    const locationId = radio.getIn(['location', 'id']);

    if (!locationId) return;

    editRadioLocation({ id: locationId, deleted: false }).then(result => {
      if (result.error) {
        addToast({ title: 'Restore Failed', message: 'Could not restore radio.', level: 'error', dismissAfter: 5000 });
      } else {
        addToast({ title: 'Restored', message: 'Radio has been restored.', level: 'success', dismissAfter: 3000 });
        this.reload();
      }
    });
  };

  getKoreActionButton = () => {
    const { radio, koreLoading } = this.state;
    const koreStatus = radio.get('kore_status', null);
    const iccid = radio.get('iccid', null);

    // Only show action buttons if we have a known kore_status
    if (!koreStatus) {
      return null;
    }

    const isVerizon = this.isVerizonSim(iccid);
    const normalizedStatus = koreStatus.toLowerCase();

    // No actions for scrapped/pending scrap statuses
    if (['scrapped', 'pending scrap', 'purged'].includes(normalizedStatus)) {
      return null;
    }

    if (['active', 'test'].includes(normalizedStatus)) {
      // Show Deactivate or Suspend button
      const action = isVerizon ? 'suspend' : 'deactivate';
      const label = isVerizon ? 'Suspend' : 'Deactivate';
      return (
        <Button variant="contained" color="error" onClick={() => this.handleKoreAction(action)} disabled={koreLoading}>
          {label}
        </Button>
      );
    } else if (['inactive', 'deactivated', 'suspended'].includes(normalizedStatus)) {
      // Show Reactivate button (reactivate to active state)
      return (
        <Button
          variant="contained"
          color="success"
          onClick={() => this.handleKoreAction('reactivate')}
          disabled={koreLoading}
        >
          {t('support.reactivate', 'Reactivate')}
        </Button>
      );
    } else if (normalizedStatus === 'stock') {
      // Stock SIMs should activate to "test" state (backend 'activate' action does this)
      return (
        <Button
          variant="contained"
          color="success"
          onClick={() => this.handleKoreAction('activate')}
          disabled={koreLoading}
        >
          {t('support.activateTest', 'Activate (Test)')}
        </Button>
      );
    }

    return null;
  };

  render() {
    const { user } = this.props;
    const { openAssignDialog, openMoveDialog, isArchiving, radio, radioFetching, radioSearchFetching, canSeeBilling } =
      this.state;

    const radioId = radio.get('id', null);
    const iccid = radio.get('iccid', null) ?? radio.get('mac_address', null);
    const koreStatus = radio.get('kore_status', null);
    const koreVerifiedAt = radio.get('kore_verifed_at', null);
    const koreProfileName = radio.getIn(['kore_profile', 'name'], null);
    const koreProfileId = radio.getIn(['kore_profile', 'profile_id'], null);
    const koreServiceTypeName = radio.getIn(['kore_service_type', 'name'], null);
    const { koreLoading, koreError, koreActionMessage, showOmniWarningDialog } = this.state;
    const pageTitle = `Radio - ${iccid}`;

    const isStaff = user.get('is_staff', false);
    const groups = user.get('groups', []);
    const isInventoryAdmin = determineIsInventoryAdmin(groups);
    const isEngineering = determineIsODLEngineering(groups);
    const isTechSupport = determineIsTechSupport(groups);
    const isSuperUser = user.get('is_superuser', false);
    const canUseKore = isEngineering || isTechSupport || isSuperUser;
    const isReseller = user.get('groups').indexOf(1061) > -1;
    const isAtLeastSiteGroupAdmin = determineIsAtLeastSiteGroupAdmin(groups);
    const canMoveOrAssign = isStaff || isReseller || isAtLeastSiteGroupAdmin || isInventoryAdmin;

    const lastComm = radio.get('signal_strength_updated_at', null)
      ? moment(radio.get('signal_strength_updated_at'))
      : null;

    let customerId = radio.getIn(['location', 'site_group', 'customer', 'id'], null);

    if (!customerId) {
      customerId = radio.getIn(['location', 'customer', 'id'], null);
    }

    const siteId = radio.getIn(['location', 'site', 'id'], null);

    return (
      <AppNav page="support_radioSearch" notFullHeight={true} {...this.props}>
        <Box
          sx={{
            px: 'var(--page-px)',
            pt: 'var(--page-py)',
            pb: 'var(--page-gap)',
          }}
        >
          <TitleBar showBackButton title={pageTitle} />

          {/* Action row — wraps gracefully on small screens */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {!siteId && canMoveOrAssign && (
              <Fragment>
                <Button onClick={() => this.setState({ openAssignDialog: true })} disabled={openAssignDialog}>
                  {t('support.assign', 'Assign')}
                </Button>
                <AssignOrCreateDialog
                  open={openAssignDialog}
                  customerId={customerId}
                  radioId={radioId}
                  siteGroupId={radio.getIn(['location', 'site_group', 'id'], null)}
                  onClose={() => {
                    this.setState({ openAssignDialog: false });
                  }}
                />
              </Fragment>
            )}
            {canMoveOrAssign && (
              <Fragment>
                <Button onClick={() => this.setState({ openMoveDialog: true })} disabled={openMoveDialog}>
                  {t('support.move', 'Move')}
                </Button>
                <MoveInventoryDialog
                  open={openMoveDialog || isArchiving}
                  customerId={customerId}
                  radioLocationId={radio.getIn(['location', 'id'])}
                  radioBillingId={radio.getIn(['billing', 'id'])}
                  inventoryLocationId={isArchiving ? 2 : null}
                  targetSiteGroups={!isArchiving || openMoveDialog}
                  onSubmit={() => this.reload()}
                  onClose={() => {
                    this.setState({ openMoveDialog: false, isArchiving: false });
                  }}
                />
                {radio.getIn(['location', 'inventory_location', 'id'], -1) != 2 && (
                  <Button onClick={() => this.setState({ isArchiving: true })} disabled={isArchiving || openMoveDialog}>
                    {t('support.archive', 'Archive')}
                  </Button>
                )}
              </Fragment>
            )}
          </Stack>

          {(radioFetching || radioSearchFetching) && <LoadingScreen />}

          {radio.getIn(['location', 'deleted']) && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={this.handleRestore}>
                  {t('support.restore', 'Restore')}
                </Button>
              }
            >
              {t('support.radioDeleted', 'This radio has been deleted from inventory.')}
            </Alert>
          )}

          {/* Details section card */}
          <Box sx={{ ...sectionSx, mb: 2 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              {t('support.radioDetails', 'Radio Details')}
            </Typography>
            <Divider sx={{ mb: 2.5 }} />
            <Grid container spacing={3}>
              {/* Radio identity */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <SectionTitle>Identity</SectionTitle>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>ICCID/MAC:</strong> {iccid}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Radio Type:</strong> {radio.get('radio_type', '')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>IP Address:</strong> {radio.get('ip_address', '') || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone Number:</strong> {radio.get('phone_number') || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t('support.lastComm', 'Last Comm:')}</strong>{' '}
                    <span title={lastComm ? lastComm.format('L, LTS') : 'Never'}>
                      {lastComm ? lastComm.fromNow() : 'Never'}
                    </span>
                  </Typography>
                </Stack>
              </Grid>

              {/* Location */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <SectionTitle>Location</SectionTitle>
                {!radio.isEmpty() ? <RadioLocation radio={radio} /> : 'Loading...'}
              </Grid>

              {/* Connectivity (Kore) - staff only */}
              {canUseKore && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <SectionTitle>Connectivity</SectionTitle>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">
                        <strong>{t('support.carrierStatus', 'Carrier Status:')}</strong> {koreStatus || 'Unknown'}
                      </Typography>
                      {koreLoading && <CircularProgress size={16} />}
                    </Stack>
                    <Typography variant="body2">
                      <strong>{t('support.carrierProfile', 'Profile:')}</strong>{' '}
                      {koreProfileName || t('support.carrierProfileNone', 'None')}
                    </Typography>
                    {/* The carrier's own profile id, shown only when it adds
                        something the name does not — an unnamed profile
                        already reads as that id. It is what KORE's portal is
                        searched by. */}
                    {koreProfileId && koreProfileId !== koreProfileName && (
                      <Typography variant="body2" color="text.secondary">
                        {koreProfileId}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>{t('support.carrierServiceType', 'Service Type:')}</strong>{' '}
                      {koreServiceTypeName || t('support.carrierServiceTypeNone', 'None')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verified: {koreVerifiedAt != null ? formatDateTime(koreVerifiedAt, user) : 'Never'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="outlined" onClick={this.handleKoreSync} disabled={koreLoading}>
                        {t('support.sync', 'Sync')}
                      </Button>
                      {this.getKoreActionButton()}
                    </Stack>
                    {koreError && <Alert severity="error">{koreError}</Alert>}
                    {koreActionMessage && <Alert severity="info">{koreActionMessage}</Alert>}
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Box>

          {!__EXCLUDE_BILLING__ && canSeeBilling && (
            <BillingSection radio={radio} radioId={radioId} onUpdate={this.reload} isStaff={isStaff} />
          )}

          <UnifiedTimelineTable data={this.state.timelineData} />
        </Box>

        {/* OmniSIM Warning Dialog */}
        <Dialog open={showOmniWarningDialog} onClose={this.cancelOmniWarning}>
          <DialogTitle>{t('support.omniWarningTitle', 'Warning: OmniSIM Deactivation')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This is an OmniSIM (ICCID starts with 891039). Deactivating OmniSIMs may have significant consequences and
              should be done with caution.
              <br />
              <br />
              {t('support.omniWarningConfirm', 'Are you sure you want to proceed with deactivation?')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.cancelOmniWarning}>{t('actions.cancel', 'Cancel')}</Button>
            <Button onClick={this.confirmOmniWarning} color="error" variant="contained">
              {t('support.deactivateAnyway', 'Deactivate Anyway')}
            </Button>
          </DialogActions>
        </Dialog>
      </AppNav>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: getAuthUserSelector(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchRadio: radio => dispatch(fetchRadio(radio)),
    radioSpecificSearch: iccid => dispatch(radioSpecificSearch(iccid)),
    fetchRadioTimeline: (radioId, params) => dispatch(fetchRadioTimeline(radioId, params)),
    // Kore actions
    fetchKoreSync: iccid => dispatch(fetchKoreSync(iccid)),
    postKoreProvision: (iccid, action) => dispatch(postKoreProvision(iccid, action)),
    addToast: toast => dispatch(addToast(toast)),
    editRadioLocation: payload => dispatch(editRadioLocation(payload)),
  };
}

const RadioDetailViewContainer = connect(mapStateToProps, mapDispatchToProps)(RadioDetailView);

export default RadioDetailViewContainer;
